import React, { useState, useEffect } from 'react';
import { DynamicOptionsModalProps } from '../../types/product';
import { OptionType } from '../../types/optionTypes';
import { getProductOptions } from '../../services/productApi';

const DynamicTeaOptionsModal: React.FC<DynamicOptionsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  product,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [optionTypes, setOptionTypes] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);

  // Ürün seçeneklerini yükle
  useEffect(() => {
    if (isOpen && product.has_options) {
      setLoading(true);
      getProductOptions(product.id)
        .then(options => {
          setOptionTypes(options);

          // Varsayılan değerleri ayarla
          const defaults: Record<string, string> = {};
          options.forEach(option => {
            defaults[option.name] = option.default_value;
          });
          setSelectedOptions(defaults);
        })
        .catch(err => {
          console.error('Seçenek türleri yüklenemedi:', err);
          setOptionTypes([]);
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (isOpen && !product.has_options) {
      setOptionTypes([]);
      setSelectedOptions({});
      setLoading(false);
    }
  }, [isOpen, product.id, product.has_options]);

  const handleDecrease = () => setQuantity(q => Math.max(1, q - 1));
  const handleIncrease = () => setQuantity(q => q + 1);

  const handleOptionChange = (optionName: string, value: string) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionName]: value,
    }));
  };

  const handleConfirm = () => {
    onConfirm({
      quantity,
      selectedOptions,
    });

    // Başarı ekranını göster
    setShowSuccessScreen(true);
  };

  // Başarı ekranı gösterildiğinde otomatik kapanma
  useEffect(() => {
    if (showSuccessScreen) {
      const timer = setTimeout(() => {
        setShowSuccessScreen(false);
        setQuantity(1);
        setSelectedOptions({});
        onClose();
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [showSuccessScreen, onClose]);

  // Modal kapandığında başarı ekranını gizle
  useEffect(() => {
    if (!isOpen) {
      setShowSuccessScreen(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  // Başarı ekranını göster
  if (showSuccessScreen) {
    return (
      <div className="w-full h-full bg-white rounded-2xl p-4 flex flex-col justify-center items-center">
        {/* Başarı İkonu */}
        <div className="w-12 h-12 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full flex items-center justify-center animate-pulse mb-3">
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        {/* Başarı Mesajı */}
        <div className="text-center mb-3">
          <h3 className="text-base font-bold text-gray-900 mb-1">Sepete Eklendi!</h3>
          <p className="text-xs text-gray-600">{product.name} sepetinize başarıyla eklendi</p>
        </div>

        {/* Ürün Bilgisi */}
        <div className="text-center">
          <div className="flex justify-center items-center gap-1 flex-wrap">
            <span className="bg-blue-50 text-blue-600 px-2 py-1 rounded text-xs">
              {quantity} adet
            </span>
            {optionTypes.length > 0 &&
              Object.keys(selectedOptions).map(optionName => {
                const optionType = optionTypes.find(ot => ot.name === optionName);
                return optionType ? (
                  <span
                    key={optionName}
                    className="bg-emerald-50 text-emerald-600 px-2 py-1 rounded text-xs"
                  >
                    {selectedOptions[optionName]}
                  </span>
                ) : null;
              })}
          </div>
        </div>
      </div>
    );
  }

  // Loading ekranı
  if (loading) {
    return (
      <div className="w-full h-full bg-white rounded-2xl p-4 flex flex-col justify-center items-center">
        <div className="w-8 h-8 border-4 border-blue-100 border-t-blue-400 rounded-full animate-spin mb-2"></div>
        <p className="text-sm text-blue-600">Seçenekler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-white rounded-2xl p-3 flex flex-col justify-center items-center overflow-auto">
      <div className="flex flex-col gap-3 w-full items-center">
        {/* Adet Seçimi */}
        <div className="flex flex-col gap-2 w-full items-center">
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <button
              onClick={handleDecrease}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-white text-blue-400 text-base font-bold transition-all hover:bg-blue-500 hover:text-white disabled:bg-gray-100 disabled:text-gray-300 shadow-sm border border-blue-200"
              disabled={quantity === 1}
              aria-label="Azalt"
              type="button"
            >
              –
            </button>
            <span className="w-8 text-center text-lg font-bold select-none text-blue-700">
              {quantity}
            </span>
            <button
              onClick={handleIncrease}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-white text-blue-400 text-base font-bold transition-all hover:bg-blue-500 hover:text-white shadow-sm border border-blue-200"
              aria-label="Arttır"
              type="button"
            >
              +
            </button>
          </div>
        </div>

        {/* Dinamik Seçenekler */}
        {optionTypes.map((optionType, index) => (
          <div key={optionType.id} className="w-full">
            <div className="flex gap-2 w-full">
              {optionType.options.map((option, optionIndex) => (
                <button
                  key={optionIndex}
                  onClick={() => handleOptionChange(optionType.name, option)}
                  className={
                    selectedOptions[optionType.name] === option
                      ? 'flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all relative bg-blue-50 border-2 border-blue-300 text-blue-700 shadow-sm'
                      : 'flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all relative bg-white border-2 border-gray-200 text-gray-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600'
                  }
                  type="button"
                >
                  {selectedOptions[optionType.name] === option && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-full flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    </div>
                  )}
                  {option}
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Onay Butonu */}
        <button
          className={
            `w-full ${optionTypes.length > 0 ? 'mt-1' : 'mt-3'} ` +
            'bg-gradient-to-r from-blue-400 to-blue-600 text-white py-2.5 rounded-lg font-semibold text-sm shadow-lg ' +
            'hover:from-blue-500 hover:to-blue-700 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed'
          }
          onClick={handleConfirm}
          type="button"
        >
          Sepete Ekle
        </button>
      </div>
    </div>
  );
};

export default DynamicTeaOptionsModal;
